import { ReactNode } from 'react';

import type { DropdownItemBase } from './autocomplete.types';

export type SectionGroup<T> = {
	label: ReactNode;
	key: string;
	options: T[];
};

export const flattenSections = <T extends DropdownItemBase>(
	sectionedOptions: SectionGroup<T>[]
): {
	flatOptions: T[];
	sectionIdxMap: WeakMap<object, number>;
	getRealId: (id: string) => string;
} => {
	const sectionIdxMap = new WeakMap<object, number>();
	const flatOptions: T[] = [];
	sectionedOptions.forEach((group, sectionIdx) => {
		group.options.forEach(opt => {
			const copy = { ...opt, id: `${group.key}:${opt.id}` } as T;
			sectionIdxMap.set(copy, sectionIdx);
			flatOptions.push(copy);
		});
	});
	const keyPattern = new RegExp(
		`^(?:${sectionedOptions.map(s => s.key).join('|')}):`
	);
	const getRealId = (id: string) => id.replace(keyPattern, '');
	return { flatOptions, sectionIdxMap, getRealId };
};

export const buildSectionHeaders = <T extends DropdownItemBase>(
	groupedOptions: readonly T[],
	sectionedOptions: SectionGroup<T>[],
	sectionIdxMap: WeakMap<object, number>,
	query: string
): Map<number, ReactNode> => {
	const headers = new Map<number, ReactNode>();
	if (query) return headers;
	let lastSectionIdx = -1;
	groupedOptions.forEach((opt, idx) => {
		const sectionIdx = sectionIdxMap.get(opt as object) ?? -1;
		if (sectionIdx !== -1 && sectionIdx !== lastSectionIdx) {
			lastSectionIdx = sectionIdx;
			headers.set(idx, sectionedOptions[sectionIdx].label);
		}
	});
	return headers;
};
